import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ChipInput from "../../../components/ChipInput/ChipInput";
import Loading from "../../../components/Loading/Loading";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../constants/routes-path";
import { useParams, useNavigate } from "react-router-dom";
import { cameraService } from "../../../services/cameraService";
import { checkErrors, hasError } from "../../../utils/forms-utils";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import moment from "moment";
import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import DateTime from "react-datetime";
import { DATE_FORMAT, DATE_FORMAT_ISO, LOCALE_PT_BR } from "constants/date-time";

function CameraNew({ data }) {
  const textColor = useColorModeValue("gray.700", "white");
  const bgTextarea = useColorModeValue("white", "navy.900");
  const placeholderColor = useColorModeValue("gray.300", "gray.400");
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState();
  const [inputError, setInputError] = useState({});
  const showToast = useCustomToast();

  useEffect(() => {
    initFormData(data)
  }, [data])

  const initFormData = (values) => {
    setFormData({
      name: {
        validations: [{ required: true }],
        value: values?.name
      },
      brand: {
        validations: [],
        value: values?.brand
      },
      model: {
        validations: [],
        value: values?.model
      },
      installationDate: {
        validations: [],
        value: values?.installationDate ?? ""
      },
      observations: {
        validations: [],
        value: values?.observations
      },
      locations: {
        validations: [],
        value: values?.locations
      },
      referencePoints: {
        validations: [],
        value: values?.referencePoints
      },
    })
  }

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    const data = { ...formData, [name]: { ...formData[name], value } }
    setInputError(checkErrors({ formData, inputError, values: data, name }))
    setFormData(data);
  };

  const handleInstallationDateChange = (value) => {
    const m = moment(value);
    const date = m.isValid() ? m.format(DATE_FORMAT_ISO) : "";

    const updated = {
      ...formData,
      installationDate: {
        ...formData.installationDate,
        value: date,
      },
    };

    setFormData(updated);

    setInputError((prev) =>
      checkErrors({ formData: updated, inputError: prev, name: "installationDate" })
    );
  };

  const onSave = async (e) => {
    if (hasError({ formData, inputError })) {
      return
    }

    try {
      const dataToSave = {
        nome: formData.name.value,
        marca: formData.brand.value,
        modelo: formData.model.value,
        data_instalacao: new Date(`${formData.installationDate.value}T00:00:00`),
        data_criacao: new Date(),
        observacoes: formData.observations.value,
        locais: formData.locations?.value?.map((local) => {
          return { nome: local.label, id: local.id }
        }) ?? [],
        pontos_referencia: formData.referencePoints?.value?.map((pontos) => {
          return { nome: pontos.label, id: pontos.id }
        }) ?? []
      };

      id ? await cameraService.update(data.id, dataToSave) : await cameraService.create(dataToSave)

      showToast({
        title: t(`settings.camera.form.title-toast${data ? '-edit' : '-add'}`),
        status: 'success'
      })

      navigate(ROUTES.camerasPath)

    } catch (error) {
      showToast({ title: t(error), status: "error" });
    }
  }

  return (
    <Flex
      direction="column"
      minH="89vh"
      align="center"
      pt={{ sm: "120px", md: "75px" }}
    >
      {formData ? (
        <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
          <ListPageCardHeader
            title={t(`settings.camera.form.title${data ? '-edit' : ''}`)}
            subtitle={t(`settings.camera.form.subtitle${data ? '-edit' : ''}`)}
          />
          <CardBody px="22px">
            <Flex direction="column" w="100%">
              <Grid
                templateColumns={{ sm: "1fr", md: "repeat(2, 1fr)" }}
                templateRows={{ md: "repeat(2, 1fr)" }}
                gap="24px"
              >
                <FormControl isRequired isInvalid={inputError['name']}>
                  <FormLabel
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {t('settings.camera.form.fields.name.label')}
                  </FormLabel>
                  <Input
                    variant="main"
                    name="name"
                    maxLength={255}
                    value={formData.name.value}
                    onChange={handleChange}
                    placeholder={t('settings.camera.form.fields.name.placeholder')}
                    fontSize="sm"
                  />
                  <FormErrorMessage>
                    {t(inputError['name'])}
                  </FormErrorMessage>

                </FormControl>
                <FormControl isInvalid={inputError['brand']}>
                  <FormLabel
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {t('settings.camera.form.fields.brand.label')}
                  </FormLabel>
                  <Input
                    variant="main"
                    name="brand"
                    maxLength={255}
                    value={formData.brand.value}
                    onChange={handleChange}
                    placeholder={t('settings.camera.form.fields.brand.placeholder')}
                    fontSize="sm"
                  />
                  <FormErrorMessage>
                    {t(inputError['brand'])}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={inputError['model']}>
                  <FormLabel
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {t('settings.camera.form.fields.model.label')}
                  </FormLabel>
                  <Input
                    variant="main"
                    name="model"
                    maxLength={255}
                    value={formData.model.value}
                    onChange={handleChange}
                    placeholder={t('settings.camera.form.fields.model.placeholder')}
                    fontSize="sm"
                  />
                  <FormErrorMessage>
                    {t(inputError['model'])}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={inputError['installationDate']}>
                  <FormLabel color={textColor} fontWeight="bold" fontSize="xs" onClick={(e) => e.preventDefault()}>
                    {t('settings.camera.form.fields.installation-date.label')}
                  </FormLabel>
                  <DateTime
                    dateFormat={DATE_FORMAT}
                    timeFormat={false}
                    locale={LOCALE_PT_BR}
                    value={moment(formData.installationDate.value)}
                    renderInput={(props) => (
                      <Input {...props} fontSize="sm" variant="main" placeholder={t('settings.camera.form.fields.installation-date.placeholder')} />
                    )}
                    onChange={handleInstallationDateChange}
                  />
                  <FormErrorMessage>
                    {t(inputError['installationDate'])}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={inputError['locations']}>
                  <FormLabel
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {t('settings.camera.form.fields.locations.label')}
                  </FormLabel>
                  <ChipInput
                    label=""
                    placeholder={t('settings.camera.form.fields.locations.placeholder')}
                    value={formData.locations?.value || []}
                    onChange={(newLocations) => handleChange({ target: { name: "locations", value: newLocations } })}
                  />
                  <FormErrorMessage>
                    {t(inputError['locations'])}
                  </FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={inputError['referencePoints']}>
                  <FormLabel
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {t('settings.camera.form.fields.reference-points.label')}
                  </FormLabel>
                  <ChipInput
                    label=""
                    placeholder={t('settings.camera.form.fields.reference-points.placeholder')}
                    value={formData.referencePoints?.value || []}
                    onChange={(newReferencePoints) => handleChange({ target: { name: "referencePoints", value: newReferencePoints } })}
                  />
                  <FormErrorMessage>
                    {t(inputError['referencePoints'])}
                  </FormErrorMessage>
                </FormControl>
              </Grid>
              <Grid
                mt={"24px"}
                mb={"24px"}
                gap="24px"
              >
                <FormControl isInvalid={inputError['observations']}>
                  <FormLabel
                    fontWeight="bold"
                    fontSize="xs"
                  >
                    {t('settings.camera.form.fields.observations.label')}
                  </FormLabel>
                  <Textarea
                    minH="120px"
                    name="observations"
                    value={formData.observations.value}
                    onChange={handleChange}
                    bg={bgTextarea}
                    _placeholder={{ color: placeholderColor }}
                    placeholder={t('settings.camera.form.fields.observations.placeholder')}
                    fontSize="sm"
                  />
                  <FormErrorMessage>
                    {t(inputError['observations'])}
                  </FormErrorMessage>
                </FormControl>
              </Grid>

              <Flex justify="space-between">
                <Button
                  variant="no-effects"
                  alignSelf="flex-end"
                  w="125px"
                  bg="gray.100"
                  color="gray.700"
                  fontSize="sm"
                  onClick={() => navigate(ROUTES.camerasPath)}
                >
                  {t(`settings.camera.form.buttons.${data ? 'cancel' : 'back'}`)}
                </Button>
                <Button
                  w="125px"
                  variant="dark"
                  fontSize="sm"
                  disabled={hasError({ formData, inputError })}
                  alignSelf="flex-end"
                  type="submit"
                  _hover={{
                    bg: "grey.500",
                    color: "white"
                  }}
                  onClick={onSave}
                >
                  {t(`settings.camera.form.buttons.${data ? 'update' : 'save'}`)}
                </Button>
              </Flex>
            </Flex>
          </CardBody>
        </Card>
      ) : (
        <Loading h="300px" w="100%" />
      )}
    </Flex>
  );
}

export default CameraNew;
